import * as Sentry from "@sentry/react"
import { ToastProvider } from "@suraasa/placebo-ui"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { APIError } from "api/utils"
import GlobalState from "components/GlobalState"
import { PageWithMeta } from "components/Metadata"
import PrivateRoute from "components/PrivateRoute"
import ViewStudent from "features/Assignments/Assignment/ViewStudent"
import ViewSubmission from "features/Assignments/Check"
import ViewAssignment from "features/Assignments/ViewAssignment"
import SSO from "features/auth/SSO"
import Documents from "features/Documents"
import EQA from "features/EQA"
import LearnerDetail from "features/EQA/LearnerDetail"
import ModuleDetail from "features/EQA/ModuleDetail"
import ListStudentsOrAssignments, {
  Type,
} from "features/home/ListStudentsOrAssignments"
import Lectures from "features/Lectures"
import ViewLecture from "features/Lectures/ViewLecture"
import NotFound from "features/NotFound"
import React from "react"
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom"
import { AuthManager } from "utils/AuthManager"
import { routes } from "utils/routes"

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const EQA_ONLY_MODE = import.meta.env.VITE_EQA_ONLY_MODE === "true"

if (SENTRY_DSN) {
  Sentry.init({
    ignoreErrors: [],
    release: `${import.meta.env.VITE_SENTRY_PROJECT}@` + APP_VERSION,
    dsn: SENTRY_DSN,
    tracesSampleRate: 0,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        // Only capture errors from our own backend. This ignores errors from domains like:
        // https://suraasa-dev-cdn.azureedge.net
        // https://firebaseinstallations.googleapis.com
        failedRequestTargets: [/https:\/\/proxima\.suraasa\.com\/.*/],
      }),
      Sentry.replayIntegration({
        // Additional SDK configuration goes in here, for example:
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    sendDefaultPii: true,

    replaysSessionSampleRate: 0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 0.5,

    beforeSend(event, hint) {
      // console.log("> Logging event on Sentry", event, hint)

      // Hydrating APIErrors with more information so it's easier to debug on Sentry Dashboard
      if (event.exception?.values && event.exception.values.length > 0) {
        const exception = event.exception.values[0]
        if (exception?.type === "APIError") {
          const originalException = hint.originalException as APIError
          event.tags = {
            ...event.tags,
            "api.status_code": originalException.statusCode?.toString() ?? "",
            "api.path": originalException.path ?? "",
            "api.message": originalException.message ?? "",
          }

          let body = originalException?.__errors?.config?.data || null
          try {
            body = JSON.parse(originalException?.__errors?.config?.data)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_) {
            /* empty */
          }

          event.contexts = {
            ...event.contexts,
            API: {
              url: originalException?.__errors?.config?.url,
              body,
              method: originalException?.__errors?.config?.method,
              statusCode: originalException?.statusCode,
              ...originalException?.errors,
            },
          }

          return event
        }
      }

      return event
    },
  })
  const authInfo = AuthManager.getAuthInfo()
  if (authInfo)
    Sentry.setUser({ id: authInfo.user.uuid, email: authInfo.user.email })
}

function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  })

  const sentryCreateBrowserRouter = SENTRY_DSN
    ? Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
    : createBrowserRouter

  const router = sentryCreateBrowserRouter([
    {
      path: routes.root,
      element: <SSO />,
      children: [
        {
          path: routes.root,
          element: <PrivateRoute />,
          children: [
            {
              path: routes.root,
              element: (
                <Navigate
                  to={EQA_ONLY_MODE ? routes.eqa.list : routes.assignment.list}
                  replace
                />
              ),
            },
            ...(EQA_ONLY_MODE
              ? [
                  {
                    path: routes.eqa.list,
                    element: (
                      <PageWithMeta name={"eqa"}>
                        <EQA />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.documents,
                    element: (
                      <PageWithMeta name={"documents"}>
                        <Documents />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.eqa.learner,
                    element: (
                      <PageWithMeta name={"eqaLearner"}>
                        <LearnerDetail />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.eqa.module,
                    element: (
                      <PageWithMeta name={"eqaModule"}>
                        <ModuleDetail />
                      </PageWithMeta>
                    ),
                  },
                ]
              : [
                  {
                    path: routes.assignment.list,
                    element: (
                      <PageWithMeta name={"assignments"}>
                        <ListStudentsOrAssignments type={Type.assignment} />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.students.list,
                    element: (
                      <PageWithMeta name={"students"}>
                        <ListStudentsOrAssignments type={Type.student} />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.assignment.view,
                    element: (
                      <PageWithMeta name={"assignment"}>
                        <ViewAssignment />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.students.view,
                    element: (
                      <PageWithMeta name={"student"}>
                        <ViewStudent />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.submission.view,
                    element: (
                      <PageWithMeta name={"submission"}>
                        <ViewSubmission />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.classes.home,
                    element: (
                      <PageWithMeta name={"classes"}>
                        <Lectures />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.documents,
                    element: (
                      <PageWithMeta name={"documents"}>
                        <Documents />
                      </PageWithMeta>
                    ),
                  },
                  {
                    path: routes.classes.class,
                    element: (
                      <PageWithMeta name={"lecture"}>
                        <ViewLecture />
                      </PageWithMeta>
                    ),
                  },
                ]),
          ],
        },
      ],
    },
    {
      path: routes.pageNotFound,
      element: (
        <PageWithMeta name={"notFound"}>
          <NotFound />
        </PageWithMeta>
      ),
    },
  ])

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalState>
        <ToastProvider />
        <RouterProvider router={router} />
      </GlobalState>
    </QueryClientProvider>
  )
}

export default App
